import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Space } from 'antd';
import {
  HomeOutlined,
  BarChartOutlined,
  CrownOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import { useBusinessStore } from '../store/businessStore';
import { useTelegramViewport } from '../hooks/useTelegramViewport';
import { useRefreshOnVisibility } from '../hooks/useRefreshOnVisibility';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/shared/PullToRefreshIndicator';
import OverviewSection from '../components/overview/OverviewSection';
import OverviewSkeleton from '../components/overview/OverviewSkeleton';
import ServiceTypesSection from '../components/settings/ServiceTypesSection';
import BusinessInfoSection from '../components/settings/BusinessInfoSection';
import InterfaceLanguageCard from '../components/settings/InterfaceLanguageCard';
import LanguageSection from '../components/settings/LanguageSection';
import TimeoutSection from '../components/settings/TimeoutSection';
import LocationSection from '../components/settings/LocationSection';
import BookingSettingsSection from '../components/settings/BookingSettingsSection';
import SettingsSkeleton from '../components/settings/SettingsSkeleton';
import SubscriptionSection from '../components/subscription/SubscriptionSection';
import SubscriptionSkeleton from '../components/subscription/SubscriptionSkeleton';
import OwnerAnalyticsTab from '../components/owner/OwnerAnalyticsTab';
import OwnerAnalyticsSkeleton from '../components/owner/OwnerAnalyticsSkeleton';
import CustomersPage from './CustomersPage';
import ProvidersPage from './ProvidersPage';
import { PageLoader } from '../components/shared/PageLoader';

const { Text } = Typography;

type TabKey = 'overview' | 'analytics' | 'subscription' | 'settings';
type InternalPage = 'main' | 'customers' | 'providers';

const TAB_ITEMS: { key: TabKey; labelKey: string; icon: React.ReactNode }[] = [
  { key: 'overview',     labelKey: 'navigation.overview',     icon: <HomeOutlined /> },
  { key: 'analytics',   labelKey: 'navigation.analytics',   icon: <BarChartOutlined /> },
  { key: 'subscription', labelKey: 'navigation.subscription', icon: <CrownOutlined /> },
  { key: 'settings',     labelKey: 'navigation.settings',     icon: <SettingOutlined /> },
];

// ─── Tab wrapper with pull-to-refresh ────────────────────────────────────────

interface TabContainerProps {
  onRefresh: () => Promise<void>;
  /** Refresh paytida ko'rsatiladigan skeleton screen. */
  skeleton?: React.ReactNode;
  children: React.ReactNode;
}

function TabContainer({ onRefresh, skeleton, children }: TabContainerProps) {
  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({ onRefresh });

  return (
    <div
      ref={containerRef}
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        progress={progress}
      />
      <div style={{ padding: 16 }}>
        {isRefreshing && skeleton ? skeleton : children}
      </div>
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────────

function OverviewTab({
  onNavigateToCustomers,
  onNavigateToProviders,
}: {
  onNavigateToCustomers: () => void;
  onNavigateToProviders: () => void;
}) {
  const fetchBusinesses = useBusinessStore((s) => s.fetchBusinesses);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    await fetchBusinesses();
    setRefreshKey((k) => k + 1);
  }, [fetchBusinesses]);

  return (
    <TabContainer onRefresh={handleRefresh} skeleton={<OverviewSkeleton />}>
      <OverviewSection
        key={refreshKey}
        onNavigateToCustomers={onNavigateToCustomers}
        onNavigateToProviders={onNavigateToProviders}
      />
    </TabContainer>
  );
}

function SubscriptionTab() {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <TabContainer onRefresh={handleRefresh} skeleton={<SubscriptionSkeleton />}>
      <SubscriptionSection key={refreshKey} />
    </TabContainer>
  );
}

function AnalyticsTab() {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <TabContainer onRefresh={handleRefresh} skeleton={<OwnerAnalyticsSkeleton />}>
      <OwnerAnalyticsTab key={refreshKey} />
    </TabContainer>
  );
}

function SettingsTab() {
  const fetchBusinesses = useBusinessStore((s) => s.fetchBusinesses);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    await fetchBusinesses();
    setRefreshKey((k) => k + 1);
  }, [fetchBusinesses]);

  return (
    <TabContainer onRefresh={handleRefresh} skeleton={<SettingsSkeleton />}>
      <Space direction="vertical" size={12} style={{ width: '100%' }} key={refreshKey}>
        <div className="bl-animate-in"><InterfaceLanguageCard /></div>
        <div className="bl-animate-in bl-animate-in-delay-1"><BusinessInfoSection /></div>
        <div className="bl-animate-in bl-animate-in-delay-1"><LocationSection /></div>
        <div className="bl-animate-in bl-animate-in-delay-2"><BookingSettingsSection /></div>
        <div className="bl-animate-in bl-animate-in-delay-2"><ServiceTypesSection /></div>
        <div className="bl-animate-in bl-animate-in-delay-3"><LanguageSection /></div>
        <div className="bl-animate-in bl-animate-in-delay-3"><TimeoutSection /></div>
      </Space>
    </TabContainer>
  );
}

/**
 * Platform bot panel — bizness egasi (OWNER) uchun.
 */
function BusinessManagementPage() {
  const { t } = useTranslation('owner');
  const { selectedBusiness, businesses, fetchBusinesses, selectBusiness, clearSelection } = useBusinessStore();
  const showBackButton = businesses.length > 1;
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  // Lazy mount: tab birinchi marta tanlangach mount bo'ladi, keyin saqlanib
  // qoladi. Boshlanishda faqat default tab (overview) mount qilinadi —
  // qolgan tabning API chaqiruvlari foydalanuvchi shu tabga o'tganda ishlaydi.
  // Tabga qaytganda qayta yuklamaydi (state saqlangan).
  const [mountedTabs, setMountedTabs] = useState<Set<TabKey>>(() => new Set(['overview']));
  const [currentPage, setCurrentPage] = useState<InternalPage>('main');
  const viewportHeight = useTelegramViewport();

  // When the WebApp is re-activated (e.g. user returned after pressing
  // Android hardware back or switching apps), re-expand so Telegram gives
  // us full viewport again. Without this, the stable viewport can stay at
  // 0 and the UI collapses to a blank screen.
  const handleReopen = useCallback(() => {
    WebApp.expand();
  }, []);
  useRefreshOnVisibility(handleReopen);

  const switchTab = (key: TabKey) => {
    setActiveTab(key);
    setMountedTabs((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (selectedBusiness) return;
    if (businesses.length === 1) {
      selectBusiness(businesses[0]);
    } else if (businesses.length === 0) {
      fetchBusinesses().then((loaded) => {
        if (loaded.length === 1) selectBusiness(loaded[0]);
      });
    }
  }, []);

  // PageLoader faqat birinchi yuklashda (selectedBusiness hali tanlanmagan)
  // ko'rinadi. Pull-to-refresh paytida fetchBusinesses() store'ga
  // isLoading=true qo'yadi — agar uni ham tekshirsak butun sahifa
  // PageLoader bilan qoplanib, tabdagi skeleton ko'rinmaydi.
  if (!selectedBusiness) {
    return <PageLoader />;
  }

  if (currentPage === 'customers') {
    return <CustomersPage onBack={() => setCurrentPage('main')} />;
  }

  if (currentPage === 'providers') {
    return <ProvidersPage onBack={() => setCurrentPage('main')} />;
  }

  return (
    <div style={{
      height: viewportHeight,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'var(--bl-bg, #f5f7fa)',
    }}>
      {/* Back button header (only when multiple businesses) */}
      {showBackButton && (
        <div style={{
          flexShrink: 0,
          padding: '10px 16px 8px',
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--bl-surface, #fff)',
          borderBottom: '1px solid var(--bl-divider, #ebebeb)',
        }}>
          <button
            type="button"
            onClick={clearSelection}
            style={{
              width: 32, height: 32, borderRadius: 10,
              background: 'var(--bl-surface-soft, #f0f4f8)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: 'var(--bl-text-strong, #0f172a)',
            }}
          >
            <ArrowLeftOutlined />
          </button>
          <Text strong style={{ fontSize: 16, color: 'var(--bl-text-strong, #0f172a)' }}>
            {selectedBusiness.businessName}
          </Text>
        </div>
      )}

      {/* Tab content — lazy mount + state preservation */}
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {mountedTabs.has('overview') && (
          <div style={{ display: activeTab === 'overview' ? 'block' : 'none', height: '100%' }}>
            <OverviewTab
              onNavigateToCustomers={() => setCurrentPage('customers')}
              onNavigateToProviders={() => setCurrentPage('providers')}
            />
          </div>
        )}
        {mountedTabs.has('analytics') && (
          <div style={{ display: activeTab === 'analytics' ? 'block' : 'none', height: '100%' }}>
            <AnalyticsTab />
          </div>
        )}
        {mountedTabs.has('subscription') && (
          <div style={{ display: activeTab === 'subscription' ? 'block' : 'none', height: '100%' }}>
            <SubscriptionTab />
          </div>
        )}
        {mountedTabs.has('settings') && (
          <div style={{ display: activeTab === 'settings' ? 'block' : 'none', height: '100%' }}>
            <SettingsTab />
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        background: 'var(--bl-surface, #fff)',
        borderTop: '1px solid var(--bl-divider, #ebebeb)',
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: 0,
        zIndex: 100,
      }}>
        {TAB_ITEMS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => switchTab(tab.key)}
              style={{
                flex: 1,
                height: 64,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                position: 'relative',
              }}
            >
              {/* Active top bar */}
              {isActive && (
                <div style={{
                  position: 'absolute',
                  top: 0, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 28, height: 3,
                  borderRadius: '0 0 4px 4px',
                  background: 'var(--bl-accent, #3390ec)',
                }} />
              )}

              {/* Icon */}
              <span style={{
                fontSize: 20,
                color: isActive ? 'var(--bl-accent, #3390ec)' : 'var(--bl-text-muted, #9ca3af)',
                lineHeight: 1,
                transition: 'color 0.15s',
              }}>
                {tab.icon}
              </span>

              {/* Label */}
              <span style={{
                fontSize: 10,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'var(--bl-accent, #3390ec)' : 'var(--bl-text-muted, #9ca3af)',
                lineHeight: 1,
                transition: 'color 0.15s',
              }}>
                {t(tab.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default BusinessManagementPage;
